<template>
  <div class="annotations-list-wrapper" :class="{docked}">
    <div v-show="opened" id="annotations-list-panel" class="annotations-list-opened" :class="{docked}">
      <!-- Docked: the same header the toolbar's flyout cards use (Pipelines, Properties...),
           so the panel reads as one family with the rest of the viewer chrome. -->
      <flyout-header v-if="docked" class="dock-hd" :title="$t('annotations-list')" @close="opened = false" />
      <!-- the close control had no accessible name at all -->
      <button
        v-if="!docked"
        class="delete"
        type="button"
        :aria-label="$t('button-close')"
        @click="opened = false"
      ></button>

      <div class="annotations-list-sidebar">
        <!-- UI-7 B2: annotation-layer selector embedded in the drawer (shares Vuex
             state with the flyout Layers panel) so users pick layers where they browse.
             Both this and the ontology selector are collapsible sections, each
             compressed from its top header; Annotation Layers is collapsed by default. -->
        <section class="al-section" :class="{collapsed: layersSectionCollapsed}">
          <!-- aria-expanded/aria-controls: a collapsed v-show section is correctly absent from
               the accessibility tree, so without these a screen reader is told neither that this
               control expands something nor what. Same reason the section could not be driven in
               automated verification until it was expanded first. -->
          <button
            class="al-section-head"
            :aria-expanded="String(!layersSectionCollapsed)"
            aria-controls="al-section-layers"
            @click="toggleSection('layers')"
          >
            <i class="fas al-caret" :class="layersSectionCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'" aria-hidden="true"></i>
            <span class="al-section-title">{{ $t('annotation-layers') }}</span>
          </button>
          <div v-show="!layersSectionCollapsed" id="al-section-layers" class="al-section-body">
            <layers-panel :index="index" />
          </div>
        </section>

        <section class="al-section" :class="{collapsed: ontologySectionCollapsed}">
          <button
            class="al-section-head"
            :aria-expanded="String(!ontologySectionCollapsed)"
            aria-controls="al-section-ontology"
            @click="toggleSection('ontology')"
          >
            <i class="fas al-caret" :class="ontologySectionCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'" aria-hidden="true"></i>
            <span class="al-section-title">{{ $t('ontology') }}</span>
          </button>
          <div v-show="!ontologySectionCollapsed" id="al-section-ontology" class="al-section-body">
            <!--<b-field position="is-centered" v-if="hasTracks">
              <b-radio-button v-model="displayType" native-value="TERM" size="is-small">
                {{$t('by-term')}}
              </b-radio-button>
              <b-radio-button v-model="displayType" native-value="TRACK" size="is-small">
                {{$t('by-track')}}
              </b-radio-button>
            </b-field>-->

            <!-- Term search - same pattern as the Ontology flyout panel: OntologyTree
                 already filters via its searchString prop (wildcard match). -->
            <b-input
              v-if="isDisplayedByTerm"
              v-model="termSearchString"
              :placeholder="$t('search-placeholder')"
              size="is-small"
              icon="search"
              expanded
              class="al-term-search"
            />
            <ontology-tree
              v-if="isDisplayedByTerm"
              v-model="selectedTermsIds"
              :ontology="ontology"
              :search-string="termSearchString"
              :multiple-selection="false"
              :hidden-nodes="hiddenTermsIds"
              :additional-nodes="[noTermOption]"
              accordion
            />

            <track-tree
              v-if="!isDisplayedByTerm"
              v-model="selectedTracksIds"
              :tracks="tracks"
              :multiple-selection="false"
            />
          </div>
        </section>
      </div>

      <div class="annotations-list-container">
        <list-annotations-by v-if="currentItem" :key="currentItem.id"
          :categorization="displayType"
          :size="85"
          color="000000"
          :nb-per-page="nbPerPage"

          :prop="currentItem"

          :all-terms="terms"
          :all-users="allUsers"
          :all-images="images"
          :all-tracks="tracks"

          :multiple-terms="false"
          :no-term="noTerm"
          :multiple-track="false"
          :no-track="noTrack"

          :images-ids="[image.id]"
          :slices-ids="sliceIds"
          :users-ids="layersIds"
          :terms-ids="termsOptionsIds"
          :tracks-ids="tracksIds"
          :reviewed="false"

          :visible="opened"
          :index="index"
          :revision="revision"
          :show-details="showDetails"

          @updateTermsOrTracks="$emit('updateTermsOrTracks', $event)"
          @updateProperties="$emit('updateProperties')"
          @centerView="$emit('centerView', {annot: $event, sameView: isSameView($event)})"
          @delete="$emit('delete', $event)"
          @select="select"
        />
      </div>
    </div>

    <!-- A real button, not a div with a click handler.
         This was `<div class="opener" @click=...>`: no role, no tabindex and no key handler, so
         a keyboard user could not open the annotation list at all, and it never appeared in the
         accessibility tree as a control. -->
    <button
      v-show="!opened && !docked"
      class="opener"
      type="button"
      :aria-expanded="String(opened)"
      aria-controls="annotations-list-panel"
      @click="opened = true"
    >{{$t("annotations-list")}} <i class="fas fa-caret-up" aria-hidden="true"></i></button>
  </div>
</template>

<script>
import {UserCollection} from '@/api';

import {get} from '@/utils/store-helpers';

import constants from '@/utils/constants.js';
import FlyoutHeader from './FlyoutHeader';
import ListAnnotationsBy from '@/components/annotations/ListAnnotationsBy';
import OntologyTree from '@/components/ontology/OntologyTree';
import TrackTree from '@/components/track/TrackTree';
import LayersPanel from '@/components/viewer/panels/LayersPanel';

export default {
  name: 'annotations-list',
  components: {
    FlyoutHeader,
    ListAnnotationsBy,
    OntologyTree,
    TrackTree,
    LayersPanel,
  },
  props: [
    'index',
  ],
  data() {
    return {
      nbPerPage: 10,
      noTermOption: {id: 0, name: this.$t('no-term')},

      users: [],

      revision: 0,

      // Collapsible left-bar sections; Annotation Layers is compressed by default.
      layersSectionCollapsed: true,
      // Docked list: everything compact by default - the user opens the one section they
      // need (accordion keeps it to one). Legacy bottom drawer keeps ontology open.
      ontologySectionCollapsed: constants.VIEWER_DOCK_LAYOUT !== false,
      termSearchString: ''
    };
  },
  computed: {
    ontology: get('currentProject/ontology'),
    configUI: get('currentProject/configUI'),

    imageModule() {
      return this.$store.getters['currentProject/imageModule'](this.index);
    },
    imageWrapper() {
      return this.$store.getters['currentProject/currentViewer'].images[this.index];
    },
    viewerWrapper() {
      return this.$store.getters['currentProject/currentViewer'];
    },
    image() {
      return this.imageWrapper.imageInstance;
    },
    images() {
      return [this.image];
    },
    isActiveImage() {
      return this.viewerWrapper.activeImage === this.index;
    },
    slices() {
      return this.imageWrapper.activeSlices;
    },
    sliceIds() {
      return this.slices.map(slice => slice.id);
    },

    isDisplayedByTerm() {
      return this.displayType === 'TERM';
    },
    items() {
      return (this.isDisplayedByTerm) ? this.termsOptions : this.tracks;
    },
    currentItem() {
      if (this.isDisplayedByTerm) {
        const found = this.termsOptions.find(term => term.id === this.selectedTermId);
        if (!found) {
          return found;
        }
        // Selecting a ROOT/parent term means the whole group (user request): browse the
        // annotations of every descendant term, not just the parent's own (usually none).
        const descendants = this.collectDescendantTermIds(found.id);
        if (descendants.length) {
          return {...found, termIds: [found.id, ...descendants]};
        }
        return found;
      } else {
        return this.tracks.find(track => track.id === this.selectedTrackId);
      }
    },

    additionalNodes() {
      return [this.noTermOption];
    },
    terms() {
      return this.$store.getters['currentProject/terms'] || [];
    },
    hiddenTermsIds() {
      return this.$store.getters[this.imageModule + 'hiddenTermsIds'];
    },
    termsOptions() {
      return [...this.terms, ...this.additionalNodes].filter(term => !this.hiddenTermsIds.includes(term.id));
    },
    termsOptionsIds() {
      return this.termsOptions.map(term => term.id);
    },

    selectedTermId() {
      return (this.selectedTermsIds.length > 0) ? this.selectedTermsIds[0] : null;
    },
    noTerm() {
      return this.isDisplayedByTerm ? this.selectedTermId === this.noTermOption.id : this.termsOptionsIds.includes(this.noTermOption.id);
    },

    tracks() {
      return this.imageWrapper.tracks.tracks;
    },
    tracksIds() {
      let optionsIds = this.tracks.map(track => track.id);
      optionsIds.push(0); // Add 0 for "no track" option
      return optionsIds;
    },
    hasTracks() {
      return this.tracks.length > 0;
    },
    selectedTrackId() {
      return (this.selectedTracksIds.length > 0) ? this.selectedTracksIds[0] : null;
    },
    noTrack() {
      return this.isDisplayedByTerm;
    },

    layers() {
      let layers = this.imageWrapper.layers.selectedLayers || [];
      layers = layers.filter(layer => layer.visible);
      return layers;
    },
    layersIds() {
      return this.layers.map(layer => layer.id);
    },
    allUsers() {
      return this.users.concat(this.userJobs);
    },

    docked() {
      // Dock layout (2026-09-03): the list is a left column inside the map cell that pushes
      // the slide right, opened from the viewer rail. VIEWER_DOCK_LAYOUT=false restores the
      // bottom drawer + pull tab.
      return constants.VIEWER_DOCK_LAYOUT !== false;
    },
    opened: {
      get() {
        return this.imageWrapper.annotationsList.open;
      },
      set(value) {
        this.$store.commit(this.imageModule + 'setShowAnnotationsList', value);
      }
    },

    displayType: {
      get() {
        return this.imageWrapper.annotationsList.displayType;
      },
      set(value) {
        this.$store.commit(this.imageModule + 'setDisplayType', value);
      }
    },

    selectedTermsIds: {
      get() {
        return this.imageWrapper.annotationsList.selectedTermsIds;
      },
      set(value) {
        this.$store.commit(this.imageModule + 'setSelectedTermsIds', value);
      }
    },

    selectedTracksIds: {
      get() {
        return this.imageWrapper.annotationsList.selectedTracksIds;
      },
      set(value) {
        this.$store.commit(this.imageModule + 'setSelectedTracksIds', value);
      }
    },

    showDetails() {
      return this.configUI['project-explore-annotation-main'];
    }
  },
  watch: {
    opened(open) {
      // One dock at a time: opening the annotation list closes the docked Images tray.
      if (open && this.docked) {
        const viewerModule = this.$store.getters['currentProject/currentViewerModule'];
        const viewer = this.$store.getters['currentProject/currentViewer'];
        if (viewer && viewer.imageSelector) {
          this.$store.commit(viewerModule + 'setImageSelector', false);
        }
      }
      this.$nextTick(() => this.$eventBus.$emit('updateMapSize'));
    },
    hasTracks(value) {
      if (!value) {
        this.displayType = 'TERM';
      }
    }
  },
  methods: {
    collectDescendantTermIds(termId) {
      const byParent = {};
      (this.terms || []).forEach(t => {
        (byParent[t.parent] = byParent[t.parent] || []).push(t);
      });
      const out = [];
      const walk = (id) => (byParent[id] || []).forEach(t => {
        out.push(t.id);
        walk(t.id);
      });
      walk(termId);
      return out;
    },
    /* Docked accordion: expanding one sidebar section immediately collapses the other, so
       the freed space always goes to what the user just opened. Legacy (bottom drawer)
       keeps the independent toggles. */
    toggleSection(which) {
      if (which === 'layers') {
        this.layersSectionCollapsed = !this.layersSectionCollapsed;
        if (!this.layersSectionCollapsed && this.docked) {
          this.ontologySectionCollapsed = true;
        }
      } else {
        this.ontologySectionCollapsed = !this.ontologySectionCollapsed;
        if (!this.ontologySectionCollapsed && this.docked) {
          this.layersSectionCollapsed = true;
        }
      }
    },
    async fetchUsers() { // TODO in vuex (project module)
      this.users = (await UserCollection.fetchAll()).array;
    },
    addAnnotationHandler(annotation) {
      if (annotation.image === this.image.id) {
        this.revision++;
      }
    },
    reloadAnnotationsHandler(idImage) {
      if (idImage === null || idImage === this.image.id) {
        this.revision++;
      }
    },
    editAnnotationHandler(updatedAnnot) {
      if (updatedAnnot.image === this.image.id) {
        this.revision++;
      }
    },
    deleteAnnotationHandler(deletedAnnot) {
      if (deletedAnnot.image === this.image.id) {
        this.revision++;
      }
    },
    isSameView(annot) {
      return this.displayType === 'TERM' && this.sliceIds.includes(annot.slice);
    },
    select({annot, options}) {
      this.$emit('select', {annot, options: {trySameView: options.trySameView || this.isSameView(annot)}});
    },

    shortkeyHandler(key) {
      if (!this.isActiveImage) {
        return;
      }

      if (key === 'toggle-annotations') {
        this.opened = !this.opened;
      }
    }
  },
  async created() {
    if (this.selectedTermsIds.length === 0) {
      this.selectedTermsIds = (this.termsOptionsIds.length > 0) ? [this.termsOptionsIds[0]] : [];
    }
    if (this.selectedTracksIds.length === 0) {
      this.selectedTracksIds = (this.hasTracks) ? [this.tracks[0].id] : [];
    }

    await this.fetchUsers();
  },
  mounted() {
    this.$eventBus.$on('addAnnotation', this.addAnnotationHandler);
    this.$eventBus.$on('reloadAnnotations', this.reloadAnnotationsHandler);
    this.$eventBus.$on('editAnnotation', this.editAnnotationHandler);
    this.$eventBus.$on('deleteAnnotation', this.deleteAnnotationHandler);
    this.$eventBus.$on('shortkeyEvent', this.shortkeyHandler);
  },
  beforeDestroy() {
    // unsubscribe from all events
    this.$eventBus.$off('addAnnotation', this.addAnnotationHandler);
    this.$eventBus.$off('reloadAnnotations', this.reloadAnnotationsHandler);
    this.$eventBus.$off('editAnnotation', this.editAnnotationHandler);
    this.$eventBus.$off('deleteAnnotation', this.deleteAnnotationHandler);
    this.$eventBus.$off('shortkeyEvent', this.shortkeyHandler);
  }
};
</script>

<style scoped>
.annotations-list-opened {
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);
  background: #fff;
  height: 25vh;
  pointer-events: auto;
  display: flex;
}

/* Dock layout: the wrapper stops being a box entirely (display:contents), so the panel
   itself becomes a flex item of .map-container and can sit as the FIRST column (order:-1),
   pushing the OpenLayers map right. Sections stack above the list in the column. */
.annotations-list-wrapper.docked {
  display: contents;
}

.annotations-list-opened.docked ::v-deep .flyout-hd {
  margin: 0;
}

.annotations-list-opened.docked {
  order: -1;
  position: static;
  /* Responsive: browser zoom = fewer CSS pixels = a smaller screen. A fixed 420px ate
     39% of the viewport at 150% zoom and HALF an iPad portrait. 26.25vw keeps the exact
     desktop look at 1600px and shrinks proportionally below it. */
  width: clamp(260px, 26.25vw, 420px);
  min-width: clamp(260px, 26.25vw, 420px);
  height: 100%;
  flex-direction: column;
  box-shadow: none;
  border-right: 1px solid #e5e8ee;
}

/* Docked: the sidebar is a flex split - each section owns its space and its own scrollbar,
   and collapsing one hands its room to the other (and, with both collapsed, to the
   annotation list below). */
.annotations-list-opened.docked .annotations-list-sidebar {
  min-width: 0;
  width: 100%;
  max-height: 55%;
  flex: 0 1 auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-bottom: 1px solid #e5e8ee;
}

/* Sections size by CONTENT (flex-basis 0 inside the content-sized sidebar computed to zero
   height - both sections rendered blank). Each body is capped and scrolls on its own; when
   the sidebar hits its 55% cap the sections shrink proportionally, and collapsing one hands
   its room to the other. */
.annotations-list-opened.docked .al-section {
  flex: 0 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.annotations-list-opened.docked .al-section.collapsed {
  flex: 0 0 auto;
}

.al-term-search {
  margin: 2px 0 8px;
}

.annotations-list-opened.docked .al-section-body {
  flex: 0 1 auto;
  min-height: 0;
  max-height: 32vh;
  overflow-y: auto;
  padding-right: 4px;
}

.annotations-list-opened.docked .annotations-list-container {
  flex: 1;
  min-height: 0;
  padding-right: 0;
  border-bottom: 0;
}

.delete {
  position: absolute;
  right: 25px;
  top: 7px;
  z-index: 10;
}

.annotations-list-container {
  overflow: auto;
  position: relative;
  border-bottom: 1px solid #ccc;
  height: 100%;
  width: 100%;
  padding-right: 25px;
}

/* UI-5 (5d): Annotations pull-tab — white rounded-top tab, centred at the
   bottom as a pair with the Images tab. Full-float viewer: the wrapper now
   spans the full width (right:0), so left:50% is the true map centre;
   offset +4px right so the pair straddles centre with an 8px gap. */
.opener {
  /* It became a <button> for keyboard access; these keep it looking exactly as the div did. */
  font: inherit;
  color: inherit;
  cursor: pointer;
  background: #fff;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(4px);
  width: auto;
  padding: 0.5rem 1.1rem;
  border: 1px solid #e5e8ee;
  border-bottom: 0;
  border-radius: 10px 10px 0 0;
  box-shadow: 0 -4px 14px rgba(15, 17, 22, 0.06);
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  text-align: center;
  text-transform: uppercase;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.14em;
  color: #5a6172;
  cursor: pointer;
  pointer-events: auto;
}

.opener:hover {
  background: #fafbfc;
  color: #0e1116;
}

.opener .fas {
  margin-left: 0;
  font-size: 12px;
  line-height: 10px;
  color: #5a6172;
}

.opener:hover .fas {
  color: #1e63d0;
}

.box {
  background: unset;
  border-radius: unset;
  box-shadow: unset;
  padding: 0.75rem;
  height: 100%;
}

>>> h2 {
  margin-bottom: 0;
}

.annotations-list-sidebar {
  padding: 10px;
  overflow-y: auto;
  min-width: 18em;
}

/* UI-7 B2: collapsible left-bar sections (Annotation Layers, Ontology), each
   compressed from its top header. */
.al-section {
  border-bottom: 1px solid #e5e8ee;
  padding-bottom: 8px;
  margin-bottom: 8px;
}
.al-section:last-child {
  border-bottom: 0;
  margin-bottom: 0;
}
.al-section-head {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  background: none;
  border: 0;
  padding: 4px 0;
  cursor: pointer;
  text-align: left;
}
.al-caret {
  font-size: 10px;
  width: 10px;
  color: #5a6172;
}
.al-section-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5a6172;
}
.al-section-head:hover .al-caret,
.al-section-head:hover .al-section-title {
  color: #0e1116;
}
.al-section-body {
  padding-top: 6px;
}

>>> ul.pagination-list {
  justify-content: flex-end;
}
</style>
